import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Modal, Button, Text, Stack, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import AuthRequestPage from './pages/AuthRequestPage';
import AuthSSOCallbackPage from './pages/AuthSSOCallbackPage';
import AuthTermsPage from './pages/AuthTermsPage';
import AuthVerifyPage from './pages/AuthVerifyPage';
import EscalationHistoryPage from './pages/EscalationHistoryPage';
import EscalationStatusPage from './pages/EscalationStatusPage';
import FormPage from './pages/FormPage';
import NotFoundPage from './pages/NotFoundPage';
import ParticipationPage from './pages/ParticipationPage';
import ParticipationSuccessPage from './pages/ParticipationSuccessPage';
import EscalationPortalPage from './pages/EscalationPortalPage';
import ProductSelectorPage from './pages/ProductSelectorPage';
import PublicFormPage from './pages/PublicFormPage';
import PublicResultPage from './pages/PublicResultPage';
import SubmissionResultPage from './pages/SubmissionResultPage';
import VoucherPage from './pages/VoucherPage';
import PatientUploadPage from './pages/PatientUploadPage';
import VoucherResultPage from './pages/VoucherResultPage';
import ProgramConfigGate from './components/ProgramConfigGate';
import { getPortalPathInfo, PATIENT_PORTAL_SEGMENT } from './utils/portalPath';
import { readPublicResultToken, resolveEnrollmentProduct } from './utils/enrollmentProduct';
import { setTermsOutdatedModalHandler } from './api/http';
import { logoutSession, buildSessionScopeKey, clearSessionExpiry } from './api/auth';
import { useResolvedProgram } from './hooks/useResolvedProgram';

function extractBasename(): string {
  return getPortalPathInfo().basename;
}

function TermsOutdatedModal() {
  const [opened, setOpened] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTermsOutdatedModalHandler(() => setOpened(true));
  }, []);

  const handleContinue = async () => {
    setIsLoggingOut(true);
    
    // Clear local session expiry
    const pathInfo = getPortalPathInfo();
    const scopeKey = buildSessionScopeKey(pathInfo.clientName, pathInfo.programName);
    clearSessionExpiry(scopeKey);
    
    try {
      // Log out to clear the session cookie on the backend
      await logoutSession();
    } catch (error) {
      console.error('Logout failed:', error);
      // Continue anyway since the session is already invalid
    }
    
    setIsLoggingOut(false);
    setOpened(false);
    navigate('/auth/request', { replace: true });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      title={
        <Stack gap="xs">
          <IconAlertCircle size={32} color="#fa5252" />
          <Title order={3}>Terms & Conditions Updated</Title>
        </Stack>
      }
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text>
          We've updated our Terms and Conditions. Please sign in to review and accept.
        </Text>
        <Button onClick={handleContinue} fullWidth loading={isLoggingOut} color="#00A57A">
          Continue to Sign In
        </Button>
      </Stack>
    </Modal>
  );
}

export default function App() {
  const basename = extractBasename();
  return (
    <BrowserRouter basename={basename === '/' ? undefined : basename}>
      {basename === '/' ? <NotFoundPage /> : <AppContent />}
    </BrowserRouter>
  );
}

function AppContent() {
  // The patient upload portal (ESCIQ-198) is login-free and has only the root route. Any other
  // path under /p must not reach the session-based program pages (which redirect to /auth/request).
  if (getPortalPathInfo().basename === `/${PATIENT_PORTAL_SEGMENT}`) {
    return (
      <div className="portal-app">
        <ProgramConfigGate>
          <Routes>
            <Route path="/" element={<SolutionRootRouter />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ProgramConfigGate>
      </div>
    );
  }

  return (
    <>
      <TermsOutdatedModal />
      <div className="portal-app">
        <ProgramConfigGate>
          <Routes>
            <Route path="/" element={<SolutionRootRouter />} />
            <Route path="/enrollments" element={<EnrollmentPageRouter />} />
            <Route path="/enrollments/:product" element={<EnrollmentPageRouter />} />
            <Route path="/escalations" element={<FormPage siteType="escalations" />} />
            <Route path="/voucher-participation-request" element={<ParticipationPage />} />
            <Route path="/voucher-participation-request/success" element={<ParticipationSuccessPage />} />
            <Route path="/auth/request" element={<AuthRequestPage />} />
            <Route path="/auth/verify" element={<AuthVerifyPage />} />
            <Route path="/auth/terms" element={<AuthTermsPage />} />
            <Route path="/auth/sso/callback" element={<AuthSSOCallbackPage />} />
            <Route path="/enrollments/:product/result" element={<PublicEnrollmentResultRouter />} />
            <Route path="/enrollments/:product/result/:reference" element={<MultiProductEnrollmentResultRouter />} />
            <Route path="/enrollments/result/:resultId" element={<SubmissionResultPage siteType="enrollments" />} />
            <Route path="/escalations/result/:resultId" element={<SubmissionResultPage siteType="escalations" />} />
            <Route path="/escalations/history" element={<EscalationHistoryPage />} />
            <Route path="/escalations/history/:resultId" element={<EscalationStatusPage />} />
            <Route path="/voucher-submissions/result/:resultId" element={<VoucherResultPage />} />
            <Route path="/voucher-submissions/:product" element={<VoucherPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ProgramConfigGate>
      </div>
    </>
  );
}

function SolutionRootRouter() {
  const { data: config, isLoading } = useResolvedProgram();

  if (isLoading) {
    return null;
  }

  switch (config?.solution) {
    case 'copay-enrollment':
      return <Navigate to="/enrollments" replace />;
    case 'bi-escalation':
      return <Navigate to="/escalations" replace />;
    case 'copay-enrollment-and-bi-escalation':
    case 'escalationiq':
      return <EscalationPortalPage />;
    case 'multi-product-copay-enrollment':
    case 'multi-product-voucher-submission':
      return <ProductSelectorPage config={config} />;
    case 'patient-upload-portal':
      return <PatientUploadPage />;
    case 'voucher-participation-request':
      return <Navigate to="/voucher-participation-request" replace />;
    default:
      return <NotFoundPage />;
  }
}

function EnrollmentPageRouter() {
  const { data: config, isLoading } = useResolvedProgram();

  if (isLoading) {
    return null;
  }

  if (config?.solution === 'multi-product-copay-enrollment' && config.authenticationRequired === false) {
    return <PublicFormPage />;
  }

  return <FormPage siteType="enrollments" />;
}

// Authenticated result route for multi-product-copay-enrollment programs; the reference is a
// result ID. Single-product enrollments use /enrollments/result/:resultId.
function MultiProductEnrollmentResultRouter() {
  const { data: config, isLoading } = useResolvedProgram();
  const { product = '', reference = '' } = useParams<{ product: string; reference: string }>();

  if (isLoading) {
    return null;
  }

  // Public programs use the #token= fragment route; a path segment is never a public token.
  if (config?.solution !== 'multi-product-copay-enrollment' || config.authenticationRequired === false) {
    return <NotFoundPage />;
  }

  return <SubmissionResultPage siteType="enrollments" resultId={reference} product={product} />;
}

// Public result route for multi-product-copay-enrollment programs:
// /enrollments/:product/result#token=<token>. The token stays in the fragment (bookmarkable,
// never sent to a server) and PublicResultPage sends it to the API in a POST body.
function PublicEnrollmentResultRouter() {
  const { data: config, isLoading } = useResolvedProgram();
  const { product = '' } = useParams<{ product: string }>();
  const { hash } = useLocation();

  if (isLoading) {
    return null;
  }

  if (config?.authenticationRequired !== false || !resolveEnrollmentProduct(config, product)) {
    return <NotFoundPage />;
  }

  return <PublicResultPage product={product} publicToken={readPublicResultToken(hash)} />;
}
